/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any --
 * The report document and pcg-report core are loose-typed by design: the
 * core is vendored prototype JS with a hand-written surface .d.ts. */

/**
 * Medaica Report page — patient view and provider document at prototype
 * fidelity, and the edit mode: password-gated editing over the extracted
 * pcg-report rules, persisted as immutable heart_report versions, each
 * carrying a lifecycle status (in_review / approved / released; a report
 * with no saved version is "preliminary").
 *
 * All report chrome (view pills, Edit, Print, Help, lifecycle stepper)
 * renders inside the report card through HeartReport's slots, matching the
 * prototype page; the portal contributes the page header, the Back button
 * and a provenance line above the card.
 *
 * The report always works from a review snapshot (an export). Load rules:
 * - no saved version: derive from the latest snapshot (nothing is saved)
 * - saved version derived from the latest snapshot: load it as is
 * - saved in_review version derived from an older snapshot: rebase it onto
 *   the latest snapshot (edits kept, flagged where values moved), save the
 *   result, show it
 * - saved approved or released version derived from an older snapshot:
 *   show it unchanged with a warning and an explicit "Reopen report"
 *   action, which rebases, saves as in_review (the exam goes back to
 *   unreviewed) and enters edit mode
 *
 * Every card carries whether it is in the report. Evidence accepted in the
 * review workspace arrives included; a card with nothing accepted behind it
 * waits in edit mode for the clinician to save an assessment or exclude it.
 * The report views, the delivered documents and approval use the included
 * cards only. Unlocking the report opens every included card for editing.
 * When the accepted Timing recordings disagree on the heart rate the four
 * timing cards arrive pending, showing the complete accepted range: the
 * clinician chooses which recordings each card includes, adjusts the value
 * if needed, and saves or excludes each card — or returns to the Medaica Viewer
 * (the page's Back to review, "Return to Viewer" while editing) to
 * change which recordings are accepted for Timing. A draft whose roll-up
 * predates the current rules is rebased onto its own snapshot when it is
 * opened.
 *
 * Status transitions from the page:
 * - edit mode: Save & Exit saves the draft (in_review) and returns to the
 *   report view — on an approved report it first confirms that the approval
 *   is withdrawn; Discard changes reloads the saved state; on an approved
 *   report with nothing changed, Revert to draft withdraws the approval.
 *   Approve report is shown but disabled while editing
 * - view mode: Approve report (approved), enabled once every card has been
 *   saved or excluded and at least one is included; Release to patient
 *   (approved → released) and Revert release (released → approved); a
 *   released report cannot be edited until its release is reverted
 *
 * Releasing sends the patient-facing document and the provider document
 * along with the save: the server freezes each as a PDF and emails the
 * patient a download link for both (the provider report is theirs to share
 * with their own doctor), so the release confirmation says that both go to
 * the patient, names the (masked) recipient, and refuses to proceed when the
 * patient profile has no email address. Reverting the release
 * stops the patient's link working.
 */

import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom"
import { examsBasePath } from "const"
import moment from "moment"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import PrintIcon from "@mui/icons-material/Print"
import Page from "@medaica/common/components/page/page"
import { backLinkSx } from "./back-link"
import { examDemographics } from "./exam-demographics"
import Loading from "@medaica/common/components/loading"
import useGlobalContext from "@medaica/common/hooks/global-context"
import useInterfaceSize from "@medaica/common/hooks/interface-size"
import useAuth from "hooks/auth"
import useBackLink from "hooks/back-link"
import { Exam, examPatientOf } from "@medaica/common/types"
import { useSnackbar } from "notistack"
import { logError } from "@medaica/common/services/util"
import {
  approvalBlockReason,
  clearFeatureApprovals,
  clearReportApproval,
  createReportFromReviewExport,
  ExamReportRollup,
  HEART_REPORT_APPROVAL_STATEMENT,
  currentHeartRateBpm,
  isCardiacCycleTimingFeature,
  isRhythmRegularityFeature,
  isRollupCurrent,
  rhythmRegularityThresholds,
  isSysDiaDurationFeature,
  outstandingReportFeatures,
  rebaseReportOntoReviewExport,
  reconcileReportInclusionStates,
  recordReportApproval,
  REVIEW_INCOMPLETE_MESSAGE,
  unresolvedEditFeatures,
} from "@medaica/common/components/pcg-report/core"
import HeartReport, { HeartReportViewMode } from "@medaica/common/components/pcg-report/HeartReport"
import HeartReportEditCards from "@medaica/common/components/pcg-report/HeartReportEditCards"
import HeartReportRecordingNotes from "@medaica/common/components/pcg-report/HeartReportRecordingNotes"
import HeartReportExamSource from "@medaica/common/components/pcg-report/HeartReportExamSource"
import HeartReportHelpDialog from "@medaica/common/components/pcg-report/HeartReportHelpDialog"
import {
  applyHeartRateRangeEdit,
  applyValueEdit,
  approveReport,
  beginAcceptedFeatureEdits,
  heartRateEvidenceRange,
  validateHeartRateRangeEdit,
  validateValueEdit,
  valueEditInitialRange,
  valueEditInitialText,
} from "@medaica/common/components/pcg-report/edit/edit-actions"
import { PcgHeartReportConflictError } from "@medaica/common/services/medaica-api-service/PcgReviewApi"
import { MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import {
  PcgHeartReport,
  PcgHeartReportStatus,
  PcgReviewExportSummary,
  PcgHeartReportSaveRequest,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"
import {
  apiErrorTypeOf,
  buildExamPatientDocument,
  buildExamProviderDocument,
  maskEmailAddress,
  responseStatusOf,
} from "./patient-delivery"

/** Prototype demo unlock code (CLINICAL_REVIEW_CODE) — client-side gate only,
 * ported as-is. Which reviewer roles may edit reports is an open policy
 * question; real authorization will replace this gate. */
const CLINICAL_REVIEW_CODE = "Medaica"

/** What is known about the latest saved version of the report (none = preliminary). */
type SavedReportInfo = {
  version: number
  status: PcgHeartReportStatus
  dateCreated: Date | null
  editorDisplayName: string | null
}

type ReadyState = {
  phase: "ready"
  report: any
  /** Latest saved version this session edits on top of; null until the first save. */
  saved: SavedReportInfo | null
  /** The review snapshot (export) the working document derives from. */
  sourceExport: PcgReviewExportSummary
  /**
   * The latest review snapshot when it is newer than the document's source —
   * only the case for an approved or released report, which is never
   * rebased without the clinician explicitly reopening it.
   */
  newerExport: PcgReviewExportSummary | null
}

type ReportState = { phase: "loading" } | { phase: "empty" } | { phase: "error"; message: string } | ReadyState

const REPORT_PIPELINE_OPTIONS = {
  formatDateTime: (value: unknown) => moment(String(value)).format("MMM D, YYYY h:mm A"),
}

/** Approved and released reports are signed off: never rebased silently, and editing withdraws the sign-off. */
const isSignedOff = (status: PcgHeartReportStatus | undefined): boolean =>
  status === "approved" || status === "released"

/** Remembers the larger clinical-review interface size between visits. */
const REVIEW_SIZE_STORAGE_KEY = "medaica-heart-report-review-size"

const savedInfoOf = (row: PcgHeartReport): SavedReportInfo => ({
  version: row.version,
  status: row.status,
  dateCreated: row.dateCreated,
  editorDisplayName: row.editorDisplayName,
})

/**
 * What the release confirmation tells the reviewer: where both documents go,
 * or — with nobody to send them to — why the report cannot be released.
 */
const releaseDialogTextFor = (recipientEmail: string | null, examLoaded: boolean): string => {
  if (recipientEmail) {
    return (
      "The patient will be emailed a link to download their Medaica Report and the provider report " +
      `(to share with their own doctor) at ${maskEmailAddress(recipientEmail)}. ` +
      "The report cannot be edited while released."
    )
  }
  if (examLoaded) {
    return (
      "This patient has no email address on file, so the report cannot be sent. " +
      "Add an email address to the patient profile, then release."
    )
  }
  return "The patient's details could not be loaded, so the report cannot be sent. Reload the page and try again."
}

/**
 * Why a release failed, in the clinician's terms: the server refuses to
 * release when the patient has no email address to send to, and answers
 * with a bad-gateway status when the patient report could not be stored.
 * Nothing was released in either case. Null for any other failure.
 */
const releaseErrorMessage = (error: unknown): string | null => {
  if (apiErrorTypeOf(error) === MedaicaApiErrorType.PATIENT_EMAIL_MISSING) {
    return "The patient has no email address on file — add one to the patient profile, then release."
  }
  if (responseStatusOf(error) === 502) {
    return "The patient report could not be stored; the report was not released."
  }
  return null
}

/**
 * How many of the snapshot's recordings feed the report (the adapter feeds
 * accepted recordings only; a snapshot with none is refused), and, when the
 * accepted Timing recordings disagreed on the heart rate, whether the
 * clinician has settled the Heart Rate card yet.
 */
const recordingsSummary = (report: unknown): string => {
  const {
    recordings = [],
    examRollup,
    features = [],
  } = report as {
    recordings?: { acceptedForExamRollup?: boolean }[]
    examRollup?: { timing?: { reviewRequired?: boolean } } | null
    features?: { name: string; reportInclusionStatus?: string }[]
  }
  const heartRate = features.find((feature) => feature.name === "Heart Rate")
  let flag = ""
  if (examRollup?.timing?.reviewRequired) {
    flag =
      heartRate?.reportInclusionStatus === "pending"
        ? " · heart-rate range awaiting a decision"
        : " · recordings varied on the heart rate"
  }
  const accepted = recordings.filter((recording) => recording.acceptedForExamRollup).length
  return `${accepted} accepted recording${accepted === 1 ? "" : "s"}${flag}`
}

/** The value dialog's state: a typed value, or — on a Heart Rate card with an accepted range — a typed range. */
type ValueEditState = {
  index: number
  value: string
  error?: string | null
  /** Present on a Heart Rate card that has an accepted range to edit. */
  evidenceRange?: { min: number; max: number } | null
  mode?: "range" | "single"
  min?: string
  max?: string
}

/** The report header's one-letter sex, or nothing when the patient's is unknown. */
const HeartReportPreviewView = (): ReactElement => {
  const { examId } = useParams<{ examId: string }>()
  // The report route exists under each exam base path (own exams and the two
  // organization exam lists); back-navigation stays on the current one
  const examsBase = examsBasePath(useLocation().pathname)
  const backToReviewLink = useBackLink(`${examsBase}/${examId ?? ""}/review`)
  const navigate = useNavigate()
  const { medaicaApiService } = useGlobalContext()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const pcgReviewApi = useMemo(() => medaicaApiService.pcgReview, [medaicaApiService])
  const [viewMode, setViewMode] = useState<HeartReportViewMode>("patient")
  const [state, setState] = useState<ReportState>({ phase: "loading" })
  // Demographics join: the exam + patientProfile.
  // Non-fatal — the report renders with coded-patient placeholders if the
  // exam fetch fails or is still in flight.
  const [exam, setExam] = useState<Exam | null>(null)

  useEffect(() => {
    let cancelled = false
    const loadExam = async (): Promise<void> => {
      if (!medaicaApiService?.exams || !examId) {
        return
      }
      try {
        const loadedExam = await medaicaApiService.exams.getExamById(examId, ["patientProfile"])
        if (!cancelled) {
          setExam(loadedExam)
        }
      } catch (error) {
        logError(error)
      }
    }
    void loadExam()
    return () => {
      cancelled = true
    }
  }, [examId, medaicaApiService])

  const demographics = useMemo(() => {
    if (!exam?.patientProfile) {
      return undefined
    }
    return {
      ...examDemographics(exam, user.organizationId),
      // The platform has no human-facing patient identifier (MRN) — the
      // prototype's free-text ID field stays an em-dash placeholder
      patientId: undefined,
    }
  }, [exam, user])

  // Edit mode: the report document is mutated in place by the extracted rule
  // layer (its contract); `revision` forces re-renders, `dirty` arms Save.
  const [editMode, setEditMode] = useState(false)
  const [revision, setRevision] = useState(0)
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [unlock, setUnlock] = useState<{ open: boolean; code: string; error: boolean }>({
    open: false,
    code: "",
    error: false,
  })
  const [valueEdit, setValueEdit] = useState<ValueEditState | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [reviewSizeLarge, toggleReviewSize] = useInterfaceSize(REVIEW_SIZE_STORAGE_KEY)
  // Confirmations: discard, reopen, saving over an approval (edit mode);
  // approve, the changed-notes notice, release and revert release (view mode)
  const [pendingConfirm, setPendingConfirm] = useState<
    | { kind: "approve" }
    | { kind: "unresolved"; names: string[] }
    | { kind: "discard" }
    | { kind: "returnToReview" }
    | { kind: "reopen" }
    | { kind: "saveOverApproval" }
    | { kind: "revertDraft" }
    | { kind: "release" }
    | { kind: "revertRelease" }
    | null
  >(null)
  // Bumped to re-run the load (discarding unsaved edits reloads the saved state)
  const [reloadCount, setReloadCount] = useState(0)

  // The document as it stood when editing began or was last saved. Edits that
  // were cancelled or put back leave the document as it was, and then there is
  // nothing to save — which matters most on an approved report, where a save
  // withdraws the approval.
  const editBaselineRef = useRef<string | null>(null)

  const markChanged = useCallback(() => {
    setRevision((current) => current + 1)
    setDirty(true)
  }, [])

  // A card entered its edit session: re-render, nothing to save yet
  const markSessionChanged = useCallback(() => {
    setRevision((current) => current + 1)
  }, [])

  // A keystroke in a note editor: arms Save right away. Deliberately no
  // re-render — the text enters the document when the editor blurs, and a
  // re-render now would reset the editor being typed in.
  const markDraftInput = useCallback(() => {
    setDirty(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    const load = async (): Promise<void> => {
      try {
        if (!examId) {
          setState({ phase: "error", message: "No exam id" })
          return
        }
        const exports = await pcgReviewApi.getExports(examId)
        if (exports.length === 0) {
          if (!cancelled) setState({ phase: "empty" })
          return
        }
        const latestExport = exports.reduce((a, b) => (a.version > b.version ? a : b))

        const savedVersions = await pcgReviewApi.getHeartReportVersions(examId)
        if (savedVersions.length === 0) {
          // Preliminary: derive from the latest snapshot; nothing is saved
          const full = await pcgReviewApi.getExport(examId, latestExport.version)
          const result = createReportFromReviewExport(full.payload, REPORT_PIPELINE_OPTIONS)
          if (cancelled) return
          if (!result.ok || !result.report) {
            const gapNotes = result.gaps.map((g) => `${g.code}: ${g.detail}`)
            setState({ phase: "error", message: `Export could not be adapted: ${gapNotes.join("; ")}` })
            return
          }
          setState({
            phase: "ready",
            report: result.report,
            saved: null,
            sourceExport: latestExport,
            newerExport: null,
          })
          return
        }

        const latestSaved = savedVersions[savedVersions.length - 1]
        const savedRow = await pcgReviewApi.getHeartReportVersion(examId, latestSaved.version)
        if (cancelled) return
        // Versions without a recorded source (older rows) count as current
        const sourceExport =
          exports.find((row) => row.version === savedRow.sourceExportVersion) ??
          (savedRow.sourceExportVersion === null ? latestExport : null)
        const isCurrent = sourceExport !== null && sourceExport.version >= latestExport.version
        // A draft whose roll-up predates the current rules is rebased onto
        // its snapshot like one whose review moved on
        const rollupCurrent = isRollupCurrent(savedRow.payload)

        if ((isCurrent && rollupCurrent) || isSignedOff(savedRow.status)) {
          // A draft saved before cards carried an inclusion state gets one
          // now; a signed-off document is shown exactly as it was signed
          if (!isSignedOff(savedRow.status)) {
            reconcileReportInclusionStates((savedRow.payload as { features: any[] }).features)
          }
          setState({
            phase: "ready",
            report: savedRow.payload,
            saved: savedInfoOf(savedRow),
            sourceExport: sourceExport ?? latestExport,
            newerExport: isCurrent ? null : latestExport,
          })
          return
        }

        // An in-review report follows the review: rebase it onto the latest
        // snapshot and persist the result before showing it
        const fullExport = await pcgReviewApi.getExport(examId, latestExport.version)
        const rebased = rebaseReportOntoReviewExport(
          savedRow.payload as any,
          fullExport.payload,
          REPORT_PIPELINE_OPTIONS
        )
        if (cancelled) return
        if (!rebased.ok) {
          const gapNotes = rebased.gaps.map((g) => `${g.code}: ${g.detail}`)
          setState({ phase: "error", message: `Export could not be adapted: ${gapNotes.join("; ")}` })
          return
        }
        const updatedRow = await pcgReviewApi.saveHeartReport(examId, {
          payload: rebased.report,
          baseVersion: savedRow.version,
          sourceExportVersion: latestExport.version,
          status: "in_review",
        })
        if (cancelled) return
        setState({
          phase: "ready",
          report: rebased.report,
          saved: savedInfoOf(updatedRow),
          sourceExport: latestExport,
          newerExport: null,
        })
        enqueueSnackbar("Report updated from the latest review")
      } catch (error) {
        logError(error)
        if (!cancelled) setState({ phase: "error", message: "Failed to load the report" })
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [enqueueSnackbar, examId, pcgReviewApi, reloadCount])

  const saveVersion = useCallback(
    async (
      status: PcgHeartReportStatus,
      successMessage: string,
      extra: Pick<PcgHeartReportSaveRequest, "approvalStatement" | "patientDocument" | "providerDocument"> = {},
      // A transition-specific explanation of a failure; the generic message otherwise
      describeError: (error: unknown) => string | null = () => null
    ): Promise<boolean> => {
      if (state.phase !== "ready" || !examId) {
        return false
      }
      setBusy(true)
      try {
        const row = await pcgReviewApi.saveHeartReport(examId, {
          payload: state.report,
          baseVersion: state.saved?.version ?? null,
          sourceExportVersion: state.sourceExport.version,
          status,
          ...extra,
        })
        setState({ ...state, saved: savedInfoOf(row) })
        editBaselineRef.current = JSON.stringify(state.report)
        setDirty(false)
        enqueueSnackbar(successMessage)
        return true
      } catch (error) {
        logError(error)
        const specificMessage = describeError(error)
        if (error instanceof PcgHeartReportConflictError) {
          enqueueSnackbar(
            "Save conflict: someone else saved this report since this session loaded. " +
              "Reload the page to pick up their version before editing further.",
            { variant: "error" }
          )
        } else if (specificMessage) {
          enqueueSnackbar(specificMessage, { variant: "error" })
        } else {
          enqueueSnackbar("There was an error saving the report", { variant: "error" })
        }
        return false
      } finally {
        setBusy(false)
      }
    },
    [enqueueSnackbar, examId, pcgReviewApi, state]
  )

  // "Approve report": a Clinician Note written against an earlier result has
  // to be resolved on its card first (the reviewer is told which cards and
  // sent back to editing); otherwise asks for the approval confirmation
  const requestApprove = useCallback(() => {
    if (state.phase !== "ready") {
      return
    }
    const unresolved = unresolvedEditFeatures((state.report as { features: any[] }).features)
    if (unresolved.length > 0) {
      setPendingConfirm({ kind: "unresolved", names: unresolved.map((feature: any) => String(feature.name)) })
      return
    }
    setPendingConfirm({ kind: "approve" })
  }, [state])

  // Opens the value dialog: a Heart Rate card with an accepted range opens
  // on its range (or on the clinician's single value once one replaced it)
  const openValueEdit = useCallback(
    (index: number) => {
      if (state.phase !== "ready") {
        return
      }
      const feature = (state.report as { features: any[] }).features[index]
      const evidenceRange = heartRateEvidenceRange(feature)
      if (!evidenceRange) {
        setValueEdit({ index, value: valueEditInitialText(feature) })
        return
      }
      const range = valueEditInitialRange(feature) ?? evidenceRange
      const single = feature.clinicianAssessmentMode === "single"
      setValueEdit({
        index,
        value: single ? valueEditInitialText(feature) : "",
        evidenceRange,
        mode: single ? "single" : "range",
        min: String(range.min),
        max: String(range.max),
      })
    },
    [state]
  )

  // Value dialog Save: a rejected value keeps the dialog open with the reason
  const saveValueEdit = useCallback(() => {
    if (!valueEdit || state.phase !== "ready") {
      return
    }
    const features = (state.report as { features: any[] }).features
    if (valueEdit.mode === "range") {
      const error = validateHeartRateRangeEdit(valueEdit.min ?? "", valueEdit.max ?? "")
      if (error) {
        setValueEdit({ ...valueEdit, error })
        return
      }
      applyHeartRateRangeEdit(features, valueEdit.index, { min: valueEdit.min ?? "", max: valueEdit.max ?? "" })
      markChanged()
      setValueEdit(null)
      return
    }
    const error = validateValueEdit(features[valueEdit.index], valueEdit.value)
    if (error) {
      setValueEdit({ ...valueEdit, error })
      return
    }
    applyValueEdit(features, valueEdit.index, valueEdit.value)
    markChanged()
    setValueEdit(null)
  }, [markChanged, state, valueEdit])

  // "Return to Viewer" (the Back button while editing): back to the
  // review workspace to change which recordings are accepted for Timing —
  // after a confirmation when edits would be lost. The review page it was
  // opened from is one step back.
  const returnToReview = useCallback(() => {
    setPendingConfirm(null)
    if (backToReviewLink.openedFromTarget) {
      navigate(-1)
    } else {
      navigate(backToReviewLink.to)
    }
  }, [backToReviewLink, navigate])

  const approve = useCallback(async (): Promise<void> => {
    setPendingConfirm(null)
    if (state.phase !== "ready") {
      return
    }
    const approved = approveReport((state.report as { features: any[] }).features)
    if (!approved.ok) {
      enqueueSnackbar(approved.reason ?? "Resolve the changed Clinician Notes before approving", { variant: "error" })
      return
    }
    // The attestation confirmed in the dialog travels twice: in the document
    // (reportApproval) and on the save request, where the server checks it
    // and records it with the approving editor
    recordReportApproval(state.report)
    markChanged()
    if (
      await saveVersion("approved", "Medaica Report approved — exam marked as reviewed", {
        approvalStatement: HEART_REPORT_APPROVAL_STATEMENT,
      })
    ) {
      setEditMode(false)
    }
  }, [enqueueSnackbar, markChanged, saveVersion, state])

  const hasUnsavedChanges = useCallback(
    (): boolean => dirty && state.phase === "ready" && JSON.stringify(state.report) !== editBaselineRef.current,
    [dirty, state]
  )

  // "Revert to draft" on an approved report: withdraw every card approval,
  // keep the clinician's edits (saved and unsaved alike) and save the result
  // as in_review — the exam goes back to unreviewed. Editing continues.
  const revertToDraft = useCallback(async (): Promise<void> => {
    setPendingConfirm(null)
    if (state.phase !== "ready") {
      return
    }
    clearFeatureApprovals((state.report as { features: any[] }).features)
    clearReportApproval(state.report)
    markChanged()
    await saveVersion("in_review", "Reverted to draft — exam marked as unreviewed")
  }, [markChanged, saveVersion, state])

  // "Save & Exit": the draft is saved as in_review and the page returns to
  // the report view; with nothing changed it just leaves. Saving over a
  // signed-off report withdraws its approval (every card approval and the
  // report's own) and the exam goes back to unreviewed — confirmed first.
  const saveAndExit = useCallback(async (): Promise<void> => {
    setPendingConfirm(null)
    if (state.phase !== "ready") {
      return
    }
    if (!hasUnsavedChanges()) {
      setDirty(false)
      setEditMode(false)
      return
    }
    const wasSignedOff = isSignedOff(state.saved?.status)
    if (wasSignedOff) {
      clearFeatureApprovals((state.report as { features: any[] }).features)
      clearReportApproval(state.report)
    }
    if (await saveVersion("in_review", wasSignedOff ? "Saved as a draft — exam marked as unreviewed" : "Draft saved")) {
      setEditMode(false)
    }
  }, [hasUnsavedChanges, saveVersion, state])

  const requestSaveAndExit = useCallback(() => {
    if (hasUnsavedChanges() && state.phase === "ready" && isSignedOff(state.saved?.status)) {
      setPendingConfirm({ kind: "saveOverApproval" })
      return
    }
    void saveAndExit()
  }, [hasUnsavedChanges, saveAndExit, state])

  // The address the released report goes to — the patient's, as the server
  // resolves it (frozen at booking for a booked exam); null when there is
  // none (or the exam could not be loaded), which blocks the release.
  const recipientEmail = exam ? (examPatientOf(exam).emailAddress || "").trim() || null : null

  // "Release to patient" (view mode): the document is saved unchanged under
  // the new status, together with the patient-facing document and the
  // provider document the server freezes and emails to the patient. Both go
  // to the patient, so they carry the patient's real name — no
  // cross-organization masking here; the provider document is otherwise
  // headed by the demographics the on-screen provider document shows.
  const release = useCallback(async (): Promise<void> => {
    setPendingConfirm(null)
    if (state.phase !== "ready" || !exam) {
      return
    }
    const patientDocument = buildExamPatientDocument(state.report, {
      patientName: examPatientOf(exam).fullName,
      examDateCreated: exam.dateCreated,
    })
    const providerDocument = buildExamProviderDocument(state.report, {
      patientName: examPatientOf(exam).fullName,
      examDateCreated: exam.dateCreated,
      demographics,
    })
    await saveVersion(
      "released",
      "Medaica Report released — the patient has been emailed a download link",
      { patientDocument, providerDocument },
      releaseErrorMessage
    )
  }, [demographics, exam, saveVersion, state])

  // "Revert release" (view mode): the document is saved unchanged as
  // approved again; the patient's download link stops working.
  const revertRelease = useCallback(async (): Promise<void> => {
    setPendingConfirm(null)
    await saveVersion("approved", "Release reverted — report is Approved")
  }, [saveVersion])

  // Entering edit mode: a document saved before cards carried an inclusion
  // state gets one now, so every card shows where it stands, and every card
  // in the report opens for editing
  const enterEditMode = useCallback(() => {
    if (state.phase === "ready") {
      const features = (state.report as { features: any[] }).features
      reconcileReportInclusionStates(features)
      beginAcceptedFeatureEdits(features)
      editBaselineRef.current = JSON.stringify(state.report)
    }
    setEditMode(true)
  }, [state])

  const requestReturnToReview = useCallback(() => {
    if (hasUnsavedChanges()) {
      setPendingConfirm({ kind: "returnToReview" })
    } else {
      returnToReview()
    }
  }, [hasUnsavedChanges, returnToReview])

  const discardEdits = useCallback(() => {
    setPendingConfirm(null)
    setDirty(false)
    setEditMode(false)
    setState({ phase: "loading" })
    setReloadCount((current) => current + 1)
  }, [])

  // "Reopen report" on a signed-off report whose review has moved on: rebase
  // onto the latest snapshot, save as in_review (the exam becomes
  // unreviewed) and go straight into edit mode. The password dialog is
  // skipped here: the clinician has just confirmed the intent to edit
  // explicitly in the reopen dialog.
  const reopen = useCallback(async (): Promise<void> => {
    setPendingConfirm(null)
    if (state.phase !== "ready" || !state.newerExport || !examId) {
      return
    }
    setBusy(true)
    try {
      const fullExport = await pcgReviewApi.getExport(examId, state.newerExport.version)
      const rebased = rebaseReportOntoReviewExport(state.report, fullExport.payload, REPORT_PIPELINE_OPTIONS)
      if (!rebased.ok) {
        const gapText = rebased.gaps.map((g) => `${g.code}: ${g.detail}`).join("; ")
        enqueueSnackbar(`The latest review could not be adapted: ${gapText}`, { variant: "error" })
        return
      }
      const row = await pcgReviewApi.saveHeartReport(examId, {
        payload: rebased.report,
        baseVersion: state.saved?.version ?? null,
        sourceExportVersion: state.newerExport.version,
        status: "in_review",
      })
      setState({
        phase: "ready",
        report: rebased.report,
        saved: savedInfoOf(row),
        sourceExport: state.newerExport,
        newerExport: null,
      })
      beginAcceptedFeatureEdits((rebased.report as { features: any[] }).features)
      setRevision((current) => current + 1)
      setDirty(false)
      editBaselineRef.current = JSON.stringify(rebased.report)
      setEditMode(true)
      enqueueSnackbar("Report reopened — exam marked as unreviewed")
    } catch (error) {
      logError(error)
      if (error instanceof PcgHeartReportConflictError) {
        enqueueSnackbar(
          "Save conflict: someone else saved this report since this session loaded. " +
            "Reload the page to pick up their version before editing further.",
          { variant: "error" }
        )
      } else {
        enqueueSnackbar("There was an error reopening the report", { variant: "error" })
      }
    } finally {
      setBusy(false)
    }
  }, [enqueueSnackbar, examId, pcgReviewApi, state])

  const report = state.phase === "ready" ? state.report : null
  // Lifecycle shown in the header-band stepper: an edit session is always
  // "in review"; otherwise the saved status, or preliminary with no save yet
  const panelState = editMode ? "in_review" : state.phase === "ready" ? state.saved?.status ?? "preliminary" : undefined

  // Prototype displaySignalQuality (L5218): Reliable→Good, Unreliable→Poor,
  // else the raw value or Fair. Shown in edit mode (admin-only upstream).
  const signalQuality = ((): string | null => {
    if (!report) {
      return null
    }
    const feature = (report as { features: any[] }).features.find((f) => f.name === "Signal Quality")
    if (!feature) {
      return null
    }
    const map: { [value: string]: string } = { Reliable: "Good", Unreliable: "Poor" }
    return map[String(feature.observed)] ?? (feature.observed ? String(feature.observed) : "Fair")
  })()

  const savedStatus = state.phase === "ready" ? state.saved?.status : undefined
  // Approval works from the saved report view: every card has to be saved or
  // excluded, and at least one included, before the report can be approved
  const features = report ? (report as { features: any[] }).features : []
  const approveBlockedBy = report ? approvalBlockReason(features) : null
  const reviewIncomplete = report ? outstandingReportFeatures(features).length > 0 : false
  const EDITING_APPROVE_HINT = "Save and exit editing before approving the report."

  // Controls row inside the card: Patient/Provider pills on the left (gone
  // in edit mode), then on the right either the Edit entry, Help, Approve
  // report (until the report is signed off; Release / Revert release after)
  // and Print (view mode), or Save & Exit, the interface-size control, Help,
  // the disabled Approve report and Discard changes (edit mode). Print
  // stands in for the prototype's download icon — exportAsHTML is
  // deliberately not ported.
  const helpButton = (
    <button
      type="button"
      className="view-toggle"
      aria-haspopup="dialog"
      title="Open the Quick Start"
      onClick={() => setHelpOpen(true)}
    >
      Help
    </button>
  )
  const controls = (
    <>
      {!editMode && (
        <div className="view-controls" role="group" aria-label="Report view">
          <button
            type="button"
            className={`view-toggle${viewMode === "patient" ? " active" : ""}`}
            onClick={() => setViewMode("patient")}
          >
            Patient Report
          </button>
          <button
            type="button"
            className={`view-toggle${viewMode === "provider" ? " active" : ""}`}
            onClick={() => setViewMode("provider")}
          >
            Provider Report
          </button>
        </div>
      )}
      <div className="phr-controls-actions">
        {editMode ? (
          <>
            {isSignedOff(savedStatus) && !dirty && (
              <button
                type="button"
                className="view-toggle"
                disabled={busy}
                title="Withdraw the approval and continue as a draft"
                onClick={() => setPendingConfirm({ kind: "revertDraft" })}
              >
                Revert to draft
              </button>
            )}
            <button type="button" className="view-toggle" disabled={busy} onClick={requestSaveAndExit}>
              Save &amp; Exit
            </button>
            <button
              type="button"
              className="view-toggle ui-size-toggle"
              aria-pressed={reviewSizeLarge}
              aria-label="Toggle larger Clinical Review text and controls"
              title="Use larger Clinical Review text and controls"
              onClick={toggleReviewSize}
            >
              <span className="ui-size-small" aria-hidden="true">
                A
              </span>
              <span className="ui-size-large" aria-hidden="true">
                A
              </span>
            </button>
            {helpButton}
            <button type="button" className="view-toggle" disabled title={EDITING_APPROVE_HINT}>
              Approve report
            </button>
            {dirty && (
              <button
                type="button"
                className="view-toggle"
                disabled={busy}
                onClick={() => setPendingConfirm({ kind: "discard" })}
              >
                Discard changes
              </button>
            )}
          </>
        ) : (
          <>
            {savedStatus !== "released" && (
              <button
                type="button"
                className="view-toggle"
                disabled={busy}
                title="Unlock report editing"
                onClick={() => setUnlock({ open: true, code: "", error: false })}
              >
                <span className="phr-lock-icon" aria-hidden="true">
                  {"\u{1F512}"}
                </span>
                <span>Edit report</span>
              </button>
            )}
            {helpButton}
            {!isSignedOff(savedStatus) && (
              <button
                type="button"
                className="view-toggle"
                disabled={busy || Boolean(approveBlockedBy)}
                title={approveBlockedBy ?? "Review and approve the final report"}
                onClick={requestApprove}
              >
                Approve report
              </button>
            )}
            {savedStatus === "approved" && (
              <button
                type="button"
                className="view-toggle"
                disabled={busy}
                onClick={() => setPendingConfirm({ kind: "release" })}
              >
                Release to patient
              </button>
            )}
            {savedStatus === "released" && (
              <button
                type="button"
                className="view-toggle"
                disabled={busy}
                onClick={() => setPendingConfirm({ kind: "revertRelease" })}
              >
                Revert release
              </button>
            )}
            <button
              type="button"
              className="download-icon-btn"
              title="Print report"
              aria-label="Print report"
              onClick={() => window.print()}
            >
              <PrintIcon fontSize="small" />
            </button>
          </>
        )}
      </div>
    </>
  )

  // Value dialog hint: both phase durations for SYS:DIA Duration; the
  // "1 : n" form and the expected resting range for SYS:DIA Ratio (its
  // normalRange is the raw ratio the card grades on, not what is typed);
  // otherwise the card's reference range when it has one
  const valueEditFeature = valueEdit ? features[valueEdit.index] : null
  const valueEditReference = valueEditFeature?.normalRange ? ` · reference ${String(valueEditFeature.normalRange)}` : ""
  let valueEditPlaceholder = `Enter observed value${valueEditReference}`
  if (valueEditFeature && isSysDiaDurationFeature(valueEditFeature)) {
    valueEditPlaceholder =
      "Enter both values, e.g. SYS 320 ms · DIA 608 ms — a range per value is accepted, e.g. SYS 278–303 ms"
  } else if (valueEditFeature && isRhythmRegularityFeature(valueEditFeature)) {
    const bands = rhythmRegularityThresholds(valueEditFeature, currentHeartRateBpm(features))
    valueEditPlaceholder =
      `Enter the S1-to-S1 timing variation, e.g. ±6.0% · Normal up to ±${bands.normal}%, ` +
      `Moderate to ±${bands.elevated}%, Elevated above`
  } else if (valueEditFeature && isCardiacCycleTimingFeature(valueEditFeature)) {
    const expected = valueEditFeature.displayReferenceRange
      ? ` · ${String(valueEditFeature.displayReferenceRange)}`
      : ""
    valueEditPlaceholder = `Enter a ratio as 1 : n, e.g. 1 : 1.8${expected}`
  }

  // Cards still waiting for the clinician: the report views show only the
  // included cards, so say that the rest of the review is in edit mode
  const banner =
    !editMode && reviewIncomplete && !isSignedOff(savedStatus) ? (
      <output className="phr-review-incomplete" data-testid="review-incomplete">
        <strong>{REVIEW_INCOMPLETE_MESSAGE}</strong>
        <span>Select Edit report to continue the review.</span>
      </output>
    ) : null

  // The release confirmation says what the patient receives — the provider
  // report too, so everything in it becomes patient-readable — and names the
  // (masked) recipient, or explains why the report cannot be sent
  const releaseDialogText = releaseDialogTextFor(recipientEmail, Boolean(exam))

  // Provenance line above the card: which review snapshot the document is
  // based on, how much of the exam feeds it, and the last save
  const provenance = ((): ReactNode => {
    if (state.phase !== "ready") {
      return null
    }
    const parts = [
      `Based on review as of ${
        state.sourceExport.dateCreated ? moment(state.sourceExport.dateCreated).format("LLL") : "—"
      }`,
      recordingsSummary(state.report),
    ]
    if (state.saved) {
      const savedAt = state.saved.dateCreated ? moment(state.saved.dateCreated).format("LLL") : "—"
      const savedBy = state.saved.editorDisplayName ? ` by ${state.saved.editorDisplayName}` : ""
      parts.push(`Last saved ${savedAt}${savedBy}`)
    }
    const approvedAt = (state.report as { reportApproval?: { approvedAt?: string } }).reportApproval?.approvedAt
    if (approvedAt && isSignedOff(state.saved?.status)) {
      parts.push(`Approved ${moment(approvedAt).format("LLL")}`)
    }
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} data-testid="heart-report-provenance">
        {parts.join(" · ")}
      </Typography>
    )
  })()

  return (
    <Page>
      {/* The way back is the portal's chevron link (as Patient Details has
          it); the report's own header band carries the brand and identity.
          While editing, the link asks first when unsaved edits would be lost. */}
      <Box sx={{ mt: 1, mb: 1.5 }}>
        {editMode ? (
          <Typography
            component="button"
            type="button"
            aria-label="Return to Viewer"
            data-testid="back-link"
            title="Return to Medaica Viewer to inspect recordings or change which recordings are accepted"
            onClick={requestReturnToReview}
            sx={backLinkSx}
          >
            <ChevronLeftIcon color="primary" />
          </Typography>
        ) : (
          <Typography
            component={RouterLink}
            to={backToReviewLink.to}
            onClick={backToReviewLink.onClick}
            aria-label="Back to review"
            data-testid="back-link"
            sx={backLinkSx}
          >
            <ChevronLeftIcon color="primary" />
          </Typography>
        )}
      </Box>
      {state.phase === "loading" && <Loading />}
      {state.phase === "empty" && (
        <Alert severity="warning">
          No review snapshot exists for this exam yet — open the Medaica Report from the Medaica Viewer page.
        </Alert>
      )}
      {state.phase === "error" && <Alert severity="error">{state.message}</Alert>}
      {state.phase === "ready" && (
        <Box>
          {state.newerExport && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  onClick={() => setPendingConfirm({ kind: "reopen" })}
                >
                  Reopen report
                </Button>
              }
            >
              {state.saved?.status === "released"
                ? "The review changed after this report was released."
                : "The review changed after this report was approved."}
            </Alert>
          )}
          {provenance}
          <HeartReport
            report={report}
            viewMode={viewMode}
            examDate={
              state.sourceExport.dateCreated ? moment(state.sourceExport.dateCreated).format("MMM D, YYYY") : undefined
            }
            showLogo
            onQuickStart={() => setHelpOpen(true)}
            demographics={demographics}
            panelState={panelState}
            controls={controls}
            editMode={editMode}
            banner={banner}
            reviewSizeLarge={reviewSizeLarge}
            signalQuality={signalQuality}
            notesSection={
              // Report notes render in every mode (and print) and are
              // editable in edit mode; the internal Viewer observations
              // show in edit mode only and never print.
              <HeartReportRecordingNotes
                notes={(report as { recordingNotes?: any[] }).recordingNotes ?? []}
                editMode={editMode}
                revision={revision}
                onChanged={markChanged}
                onDraftInput={markDraftInput}
              />
            }
          >
            {editMode ? (
              // Prototype admin container order: Exam Source chips → admin
              // cards (questionnaire and reviewer observations above and the
              // follow-up below come from the shell)
              <>
                <HeartReportExamSource
                  recordings={(report as { recordings?: any[] }).recordings ?? []}
                  examRollup={(report as { examRollup?: ExamReportRollup | null }).examRollup ?? null}
                  onNotice={(title, message) => enqueueSnackbar(`${title}: ${message}`)}
                  loadAudio={
                    medaicaApiService?.auscultations
                      ? (recordingId) =>
                          medaicaApiService.auscultations.getAuscultationRecordingPlaybackData(recordingId)
                      : undefined
                  }
                />
                <HeartReportEditCards
                  features={(report as { features: any[] }).features}
                  questionnaire={(report as { questionnaire?: unknown }).questionnaire ?? null}
                  revision={revision}
                  onChanged={markChanged}
                  onSessionChanged={markSessionChanged}
                  onDraftInput={markDraftInput}
                  onNotice={(message) => enqueueSnackbar(message, { variant: "warning" })}
                  onRequestValueEdit={openValueEdit}
                />
              </>
            ) : null}
          </HeartReport>
        </Box>
      )}

      <HeartReportHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Dialog
        open={pendingConfirm?.kind === "unresolved"}
        onClose={() => setPendingConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Resolve changed Clinician Notes before approving</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <ul style={{ margin: 0, paddingLeft: "1.25em" }}>
              {(pendingConfirm?.kind === "unresolved" ? pendingConfirm.names : []).map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            {pendingConfirm?.kind === "unresolved" && pendingConfirm.names.length === 1
              ? "The assessment on this card changed after its Clinician Note was written."
              : "The assessment on these cards changed after their Clinician Notes were written."}{" "}
            Select Edit report and choose “Keep my note” or “Use updated suggested note” on each card, then approve.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPendingConfirm(null)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingConfirm?.kind === "approve"} onClose={() => setPendingConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Medaica Report?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>{HEART_REPORT_APPROVAL_STATEMENT}</DialogContentText>
          <DialogContentText>The exam will be marked as reviewed.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void approve()}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingConfirm?.kind === "revertDraft"}
        onClose={() => setPendingConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revert to draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>The approval will be withdrawn and the exam marked as unreviewed.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void revertToDraft()}>
            Revert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingConfirm?.kind === "saveOverApproval"}
        onClose={() => setPendingConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save changes to an approved report?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The approval will be withdrawn and the exam marked as unreviewed until the report is approved again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveAndExit()}>
            Save &amp; Exit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingConfirm?.kind === "release"} onClose={() => setPendingConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Release Medaica Report to patient?</DialogTitle>
        <DialogContent>
          <DialogContentText>{releaseDialogText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button variant="contained" disabled={!recipientEmail} onClick={() => void release()}>
            Release and send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingConfirm?.kind === "revertRelease"}
        onClose={() => setPendingConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revert release?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The report returns to Approved and the patient&apos;s download link stops working. Re-releasing later sends
            the patient a new email. The exam stays marked as reviewed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void revertRelease()}>
            Revert
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingConfirm?.kind === "discard"} onClose={() => setPendingConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Keep editing</Button>
          <Button variant="contained" color="error" onClick={discardEdits}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pendingConfirm?.kind === "returnToReview"}
        onClose={() => setPendingConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Return to the Viewer?</DialogTitle>
        <DialogContent>
          <DialogContentText>Unsaved changes to this report will be lost.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Keep editing</Button>
          <Button variant="contained" color="error" onClick={returnToReview}>
            Return to Viewer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingConfirm?.kind === "reopen"} onClose={() => setPendingConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reopen Medaica Report?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A new draft will be created from the current review. Your previous edits are kept and flagged where the
            underlying values changed. The exam will be marked as unreviewed until re-approved.
          </DialogContentText>
          {savedStatus === "released" && (
            <DialogContentText sx={{ mt: 2 }}>
              The patient loses access to the previously released version. The new version must be released to the
              patient once the edits and approval are complete.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => void reopen()}>
            Reopen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prototype unlock modal: "Edit Medaica Report" */}
      <Dialog
        open={unlock.open}
        onClose={() => setUnlock({ open: false, code: "", error: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Medaica Report</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>Enter the password to unlock report editing controls.</DialogContentText>
          <TextField
            type="password"
            placeholder="Password"
            fullWidth
            autoFocus
            size="small"
            value={unlock.code}
            error={unlock.error}
            helperText={unlock.error ? "Incorrect code." : undefined}
            onChange={(event) => setUnlock({ ...unlock, code: event.target.value, error: false })}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                // Stop the Enter keypress: when the dialog closes, focus
                // returns to the button that opened it and an unconsumed
                // keypress would click it again.
                event.preventDefault()
                if (unlock.code === CLINICAL_REVIEW_CODE) {
                  setUnlock({ open: false, code: "", error: false })
                  enterEditMode()
                } else {
                  setUnlock({ ...unlock, error: true })
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlock({ open: false, code: "", error: false })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (unlock.code === CLINICAL_REVIEW_CODE) {
                setUnlock({ open: false, code: "", error: false })
                enterEditMode()
              } else {
                setUnlock({ ...unlock, error: true })
              }
            }}
          >
            Unlock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prototype "Edit Observed Value" modal. A value the card cannot take
          (SYS:DIA Duration needs both phase durations) keeps the dialog open
          with the reason under the field. A Heart Rate card with an accepted
          range edits the range as a minimum and maximum, or — an explicit
          choice — one clinician value in its place; the accepted range is
          named and kept for the audit record either way */}
      <Dialog open={valueEdit !== null} onClose={() => setValueEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{valueEdit?.evidenceRange ? "Edit Heart Rate assessment" : "Edit Observed Value"}</DialogTitle>
        <DialogContent>
          {valueEdit?.evidenceRange ? (
            <Box sx={{ mb: 1.5 }} role="group" aria-label="How to record the assessment">
              <Typography variant="caption" sx={{ display: "block", fontWeight: 700, mb: 0.5 }}>
                Choose how to record the assessment
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant={valueEdit.mode === "range" ? "contained" : "outlined"}
                  aria-pressed={valueEdit.mode === "range"}
                  onClick={() => setValueEdit({ ...valueEdit, mode: "range", error: null })}
                >
                  Edit range
                </Button>
                <Button
                  size="small"
                  variant={valueEdit.mode === "single" ? "contained" : "outlined"}
                  aria-pressed={valueEdit.mode === "single"}
                  onClick={() => setValueEdit({ ...valueEdit, mode: "single", error: null })}
                >
                  Use single clinician value
                </Button>
              </Box>
            </Box>
          ) : null}
          {valueEdit?.mode === "range" ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                label="Minimum BPM"
                type="number"
                inputProps={{ step: 0.1, min: 1 }}
                fullWidth
                autoFocus
                size="small"
                value={valueEdit.min ?? ""}
                error={Boolean(valueEdit.error)}
                onChange={(event) => setValueEdit({ ...valueEdit, min: event.target.value, error: null })}
              />
              <TextField
                label="Maximum BPM"
                type="number"
                inputProps={{ step: 0.1, min: 1 }}
                fullWidth
                size="small"
                value={valueEdit.max ?? ""}
                error={Boolean(valueEdit.error)}
                helperText={valueEdit.error ?? undefined}
                onChange={(event) => setValueEdit({ ...valueEdit, max: event.target.value, error: null })}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault()
                    saveValueEdit()
                  }
                }}
              />
            </Box>
          ) : (
            <TextField
              placeholder={valueEdit?.evidenceRange ? "Enter one clinician-assessed BPM value" : valueEditPlaceholder}
              fullWidth
              autoFocus
              size="small"
              value={valueEdit?.value ?? ""}
              error={Boolean(valueEdit?.error)}
              helperText={valueEdit?.error ?? undefined}
              onChange={(event) => valueEdit && setValueEdit({ ...valueEdit, value: event.target.value, error: null })}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  saveValueEdit()
                }
              }}
            />
          )}
          {valueEdit?.evidenceRange ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "text.secondary" }}>
              Original accepted evidence: {valueEdit.evidenceRange.min.toFixed(1)}–
              {valueEdit.evidenceRange.max.toFixed(1)} bpm. This remains in the audit record.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValueEdit(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveValueEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

export default HeartReportPreviewView
